/**
 * Where a submission lives before its turn streams (HIG-5971). The fast path
 * commits the user message + assistant placeholder to the transcript at
 * submit time; the queue path shows a dashed bubble and stages everything on
 * `initialMessage` until the held-open stream's `metadata` event. This handle
 * answers that question once, at the queue decision, so the submit flow stays
 * mode-free — every difference between the two paths lives here.
 */
import { v4 as uuidv4 } from 'uuid'
import type { ChatMessage, FinalizedAttachment, UserMessage } from '../types'
import type { QueuedMessage } from '../store'

/** The dashed-bubble queue API the submit path needs (chat/store/queue.ts). */
export type SubmitQueue = {
  getForChat(chatId: string): Array<QueuedMessage>
  enqueue(chatId: string, message: QueuedMessage): boolean
  hydrate(chatId: string, localId: string, backendId: string): void
  remove(chatId: string, localId: string): void
  isPresent(chatId: string, localId: string): boolean
  setAttachments(chatId: string, localId: string, attachments: Array<FinalizedAttachment>): void
  markAwaitingNextDrain(chatId: string): void
  clearAwaitingNextDrain(chatId: string): void
}

export type TurnPresentation = {
  /**
   * Registry key for the turn. Queued turns hold a side key so they never
   * supersede the active turn; the flow adopts the chat key at `metadata`.
   */
  readonly registryKey: string
  /** Whether the user message + assistant placeholder are in the transcript. */
  readonly committed: boolean
  /** Whether reconnecting this held-open POST would revive a live send. */
  readonly canResume: boolean
  /** Stamp fields onto the turn's user message, wherever it currently lives. */
  stampUserMessage(patch: Partial<UserMessage>): void
  /** The backend acknowledged the queued bubble. */
  onQueued(queuedId: string): void
  /** Finalized uploads are known (queue bubble's paperclip indicator). */
  onAttachmentsFinalized(attachedContext: Array<FinalizedAttachment>): void
  /** The turn started streaming — run any deferred transcript commit. */
  onMetadata(): void
  /** Attachment upload failed — undo presentation-owned optimistic state. */
  onUploadError(): void
  /** The submission settled — roll back anything that never committed. */
  onSettled(): void
}

export type TurnPresentationParams = {
  conversationId: string
  chatInput: string | undefined
  initialMessage: UserMessage
  /** Programmatic submits that must not clear/restore the composer draft. */
  preserveDraft: boolean
  /** Composer text captured before it was cleared, for cap-race/error restore. */
  inputBeforeSubmit: string
  queue: SubmitQueue
  updateLastMessage: (chatId: string, message: Partial<ChatMessage>) => void
  setChatInput: (value: string) => void
  /** The optimistic transcript commit; the flow owns what committing means. */
  commit: () => void
}

/**
 * Create the presentation for a submission. Returns null when the queue cap
 * race loses — the composer draft is restored and the submission is dropped.
 */
export function createTurnPresentation(shouldQueue: boolean, params: TurnPresentationParams): TurnPresentation | null {
  return shouldQueue ? queuedPresentation(params) : transcriptPresentation(params)
}

/** Fast path: the turn is in the transcript from the moment of submit. */
function transcriptPresentation(params: TurnPresentationParams): TurnPresentation {
  params.commit()
  return {
    registryKey: params.conversationId,
    committed: true,
    canResume: true,
    stampUserMessage: (patch) =>
      params.updateLastMessage(params.conversationId, { role: 'user', id: params.initialMessage.id, ...patch }),
    onQueued: () => {},
    onAttachmentsFinalized: () => {},
    onMetadata: () => {},
    onUploadError: () => {},
    onSettled: () => {},
  }
}

/**
 * Queue path: a dashed bubble holds the submission while the chat streams;
 * stamps stage onto `initialMessage` so the deferred commit picks them up.
 */
function queuedPresentation(params: TurnPresentationParams): TurnPresentation | null {
  const { conversationId, queue } = params
  const candidate: QueuedMessage = {
    localId: uuidv4(),
    messageId: null,
    prompt: params.chatInput ?? '',
    attachedContext: [],
    cancelling: false,
  }
  if (!queue.enqueue(conversationId, candidate)) {
    // Cap is enforced by the disabled send button; this only runs on a true
    // race. Restore the cleared input so the typed message isn't lost.
    if (!params.preserveDraft) params.setChatInput(params.inputBeforeSubmit)
    return null
  }
  const localId = candidate.localId
  let committed = false

  return {
    registryKey: `queued:${localId}:${conversationId}`,
    get committed() {
      return committed
    },
    get canResume() {
      if (committed) return true
      return queue.getForChat(conversationId).some((message) => message.localId === localId && !message.cancelling)
    },
    stampUserMessage: (patch) => {
      Object.assign(params.initialMessage, patch)
    },
    onQueued: (queuedId) => queue.hydrate(conversationId, localId, queuedId),
    onAttachmentsFinalized: (attachedContext) => {
      // Stamp finalized attachments onto the bubble so the paperclip
      // indicator can appear once uploads complete.
      if (attachedContext.length > 0) queue.setAttachments(conversationId, localId, attachedContext)
    },
    onMetadata: () => {
      // Held-open stream transitions from waiting to streaming: drop the
      // bubble and run the deferred commit. A missing bubble means the send
      // was cancelled — leave the transcript alone.
      if (committed || !queue.isPresent(conversationId, localId)) return
      queue.remove(conversationId, localId)
      committed = true
      params.commit()
    },
    onUploadError: () => {
      queue.remove(conversationId, localId)
      // No transcript entry to stamp the error onto — without restoring the
      // cleared input the typed message vanishes with no path to retry.
      if (!params.preserveDraft) params.setChatInput(params.inputBeforeSubmit)
    },
    onSettled: () => {
      // Held-open stream closed without `metadata` (cancel via DELETE,
      // network drop) — roll the bubble back. Idempotent by lookup.
      if (!committed && queue.isPresent(conversationId, localId)) {
        queue.remove(conversationId, localId)
      }
    },
  }
}
